import { z } from "zod";

import type { GetEmployeeMeetingStatsSearchServiceJsonResponse } from "@performyard/service-contracts";

import request from "./internal-base-request";

export const EmployeeMeetingStatsSortableFieldsEnum = z.enum([
	"employeeName",
	"managerName",
	"group",
	"workspaceCount",
	"allMeetingsCount",
	"oneOnOneWithManagerCount",
	"teamMeetingsCount",
]);
export type EmployeeMeetingStatsSortableFields = z.infer<typeof EmployeeMeetingStatsSortableFieldsEnum>;

const GetEmployeeMeetingStatsParamsSchema = z.object({
	name: z.string().optional(),
	employeeId: z.string().optional(),
	managerId: z.string().optional(),
	groupId: z.string().optional(),
	dateRange: z.enum(["all_time", "past", "upcoming"]).optional(),
	withoutMeetings: z.boolean().optional(),

	limit: z.number().optional(),
	skip: z.number().optional(),
	sort: z
		.object({
			name: EmployeeMeetingStatsSortableFieldsEnum,
			direction: z.enum(["asc", "desc"]),
		})
		.optional(),
});

export type GetEmployeeMeetingStatsParams = z.infer<typeof GetEmployeeMeetingStatsParamsSchema>;

// Column names map 1:1 to the search-service projected field names.
const employeeMeetingStatsSortMap: Record<EmployeeMeetingStatsSortableFields, string> = {
	employeeName: "employeeName",
	managerName: "managerName",
	group: "group",
	workspaceCount: "workspaceCount",
	allMeetingsCount: "allMeetingsCount",
	oneOnOneWithManagerCount: "oneOnOneWithManagerCount",
	teamMeetingsCount: "teamMeetingsCount",
};

/**
 * Retrieves per-employee meeting coverage statistics via the internal-api search service.
 */
export const getEmployeeMeetingStatsSearch = async (
	params: GetEmployeeMeetingStatsParams
): Promise<GetEmployeeMeetingStatsSearchServiceJsonResponse> => {
	const validatedParams = GetEmployeeMeetingStatsParamsSchema.parse(params);
	const searchParams = new URLSearchParams();

	searchParams.append("service", "employee_meeting_stats");

	if (validatedParams.name !== undefined && validatedParams.name.trim() !== "") {
		searchParams.append("name_re", validatedParams.name.trim());
	}
	if (validatedParams.employeeId !== undefined) {
		searchParams.append("id_eq", validatedParams.employeeId);
	}
	if (validatedParams.managerId !== undefined) {
		searchParams.append("manager_eq", validatedParams.managerId);
	}
	if (validatedParams.groupId !== undefined) {
		searchParams.append("department_eq", validatedParams.groupId);
	}
	if (validatedParams.dateRange !== undefined) {
		searchParams.append("date_range", validatedParams.dateRange);
	}
	if (validatedParams.withoutMeetings) {
		searchParams.append("without_meetings", "true");
	}
	if (validatedParams.limit !== undefined) {
		searchParams.append("limit", validatedParams.limit.toString());
	}
	if (validatedParams.skip !== undefined) {
		searchParams.append("skip", validatedParams.skip.toString());
	}

	if (validatedParams.sort) {
		const { name, direction } = validatedParams.sort;
		const sortName = employeeMeetingStatsSortMap[name] || name;
		searchParams.append("sort", `${direction}(${sortName})`);
	} else {
		searchParams.append("sort", "asc(employeeName)");
	}

	const queryString = searchParams.toString() ? `?${searchParams.toString()}` : "";
	const [response] = await request<GetEmployeeMeetingStatsSearchServiceJsonResponse>(`/v1/search${queryString}`);

	return response;
};
